import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { BoundTextField } from "lib/@components/binding/bound-components"
import { useMobile } from "lib/@components/mobile"
import { If } from "lib/@components/switch"
import { useYupContext } from "lib/@components/binding/use-yup-wrapper"
import { noChange, useRefresh } from "lib/@hooks/useRefresh"
import { reduceMotion } from "lib/reduce-motion"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import Iconify from "minimals-template/components/Iconify"
import { useEffect, useState } from "react"
import { isLockedToLegacy } from "authoring/lib/enum/project-phase.mjs"
import {
    isDuplicateWorkPackageNameError,
    normalizeWorkPackageDescription,
    normalizeWorkPackageName,
} from "../lib/work-package-metadata.mjs"
import { updateProject } from "../controller/update-project"

export function ProjectEditDialog({ open, setOpen, project }) {
    const projectName = project?.name ?? ""
    const projectDescription = project?.internalDescription ?? ""
    const [formValues, setFormValues] = useState({
        name: projectName,
        internalDescription: projectDescription,
    })
    const [nameError, setNameError] = useState("")
    const [showDismissWarning, setShowDismissWarning] = useState(false)
    const [dismissWarningAnimationKey, setDismissWarningAnimationKey] = useState(0)
    const [saving, setSaving] = useState(false)
    const [yupContext, validateAllFields] = useYupContext()
    const refresh = useRefresh(noChange)
    const isMobile = useMobile()
    const locked = isLockedToLegacy(project?.phase)
    const hasUnsavedChanges =
        normalizeWorkPackageName(formValues?.name) !== normalizeWorkPackageName(project?.name) ||
        normalizeWorkPackageDescription(formValues?.internalDescription) !==
            normalizeWorkPackageDescription(project?.internalDescription)
    const saveDisabled = locked || saving || !!nameError || !hasUnsavedChanges || !validateAllFields()
    const dismissWarningSx = resolveDismissWarningSx()

    useEffect(syncFormValuesWithProject, [open, project?.id, projectName, projectDescription])
    useEffect(clearDismissWarningWhenClean, [showDismissWarning, hasUnsavedChanges])

    if (!open || !project) return null

    return (
        <Dialog
            open={open}
            onClose={handleDialogClose}
            fullWidth
            maxWidth="sm"
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 3,
                    overflow: "hidden",
                },
            }}
        >
            <Bound target={formValues} onChange={refresh} yupContext={yupContext}>
                <Box data-cy="work-package-edit-dialog">
                    <DialogTitle sx={{ px: 3, pt: 3, pb: 1.5 }}>
                        <Stack spacing={2}>
                            <Typography variant="h5">Edit Work Package</Typography>

                            <If value={showDismissWarning}>
                                <Alert
                                    key={dismissWarningAnimationKey}
                                    severity="error"
                                    sx={dismissWarningSx}
                                    data-cy="project-edit-unsaved-warning"
                                >
                                    You have unsaved changes. Save them or use Cancel to discard them before closing.
                                </Alert>
                            </If>
                        </Stack>
                    </DialogTitle>
                    <DialogContent sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
                        <Stack spacing={3}>
                            <If value={locked}>
                                <Alert severity="info">
                                    This work package can&apos;t be edited after it has been sent to or published in
                                    Legacy.
                                </Alert>
                            </If>
                            <Stack spacing={2.5} pt={1}>
                                <BoundTextField
                                    label="Name"
                                    field="name"
                                    placeholder="Enter a work package name"
                                    yup="string.required.trim"
                                    error={nameError ? true : undefined}
                                    helperText={nameError}
                                    onChanged={clearNameError}
                                    disabled={locked || saving}
                                />

                                <BoundTextField
                                    label="Description"
                                    field="internalDescription"
                                    placeholder="Add a short internal description"
                                    yup="string.required.trim"
                                    multiline
                                    minRows={5}
                                    disabled={locked || saving}
                                />
                            </Stack>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3, pt: 1.5 }}>
                        <Button
                            onClick={handleCancel}
                            color="primary"
                            startIcon={<Iconify icon="material-symbols:cancel" />}
                            data-cy="dialogActionsCancelButton"
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            startIcon={<Iconify icon="mdi:tick" />}
                            disabled={saveDisabled}
                            data-cy="dialogActionsOkButton"
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Box>
            </Bound>
        </Dialog>
    )

    function resolveDismissWarningSx() {
        if (dismissWarningAnimationKey <= 0 || reduceMotion()) {
            return { mt: 2 }
        }

        return {
            mt: 2,
            animation: "dismissWarningWiggle 220ms ease-in-out",
            "@keyframes dismissWarningWiggle": {
                "0%": { transform: "translateX(0px)" },
                "20%": { transform: "translateX(-6px)" },
                "45%": { transform: "translateX(5px)" },
                "70%": { transform: "translateX(-3px)" },
                "100%": { transform: "translateX(0px)" },
            },
        }
    }

    function syncFormValuesWithProject() {
        if (!open) return

        setFormValues({
            name: projectName,
            internalDescription: projectDescription,
        })
        setNameError("")
        setDismissWarningAnimationKey(0)
        setShowDismissWarning(false)
        setSaving(false)
    }

    function clearDismissWarningWhenClean() {
        if (showDismissWarning && !hasUnsavedChanges) {
            setDismissWarningAnimationKey(0)
            setShowDismissWarning(false)
        }
    }

    function handleCancel() {
        closeDialog()
    }

    function clearNameError() {
        if (nameError) {
            setNameError("")
        }
    }

    function handleDialogClose(...dialogCloseArgs) {
        const reason = dialogCloseArgs[1]

        if ((reason === "backdropClick" || reason === "escapeKeyDown") && !locked && hasUnsavedChanges) {
            showDismissWarningBanner()
            return
        }

        closeDialog()
    }

    async function handleSave() {
        setSaving(true)
        setNameError("")

        try {
            const saved = await updateProject({
                id: project?.id,
                project: {
                    id: project?.id,
                    name: formValues?.name,
                    internalDescription: formValues?.internalDescription,
                },
            })
            if (!saved) {
                throw new Error("Failed to update work package")
            }
            successSnackbar("Work package updated")
            closeDialog()
        } catch (error) {
            if (isDuplicateWorkPackageNameError(error?.message)) {
                setNameError(error.message)
                return
            }

            errorSnackbar(error?.message ?? "Failed to update work package")
        } finally {
            setSaving(false)
        }
    }

    function closeDialog() {
        setDismissWarningAnimationKey(0)
        setShowDismissWarning(false)
        setOpen(false)
    }

    function showDismissWarningBanner() {
        if (showDismissWarning) {
            setDismissWarningAnimationKey(incrementDismissWarningAnimationKey)
            return
        }

        setDismissWarningAnimationKey(0)
        setShowDismissWarning(true)
    }

    function incrementDismissWarningAnimationKey(currentKey) {
        return currentKey + 1
    }
}
